<template>
  <div
    v-if="skeletonLoading"
    :style="{width}"
    :class="{small}"
    class="skeleton-loader"
  ></div>
  <z-card
    v-else
    :class="classes"
    :width="width"
    :shadow="shadow"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <template #content>
      <div class="card-info">
        <span class="card-title pb-0">{{ title }}</span>

        <z-circular-progress
          v-if="isLoading"
          class="mt-1"
          indeterminate
          :value="50"
          :color="color ? color : 'primary'"
          :size="small ? 20 : 32"
        ></z-circular-progress>

        <span v-else class="card-value" :style="`color: ${color}`">
          {{ value }}
        </span>
      </div>
    </template>
  </z-card>
</template>

<script>
/**
 * Esse tipo de card, é utilizado principalmente para exibir informações
 * em um dashboard ou em alguma página que contenha informações organizadas
 * de forma estruturada.
 * @displayName Card Info
 */
export default {
  name: 'ZCardInfo',
  props: {
    /**
     * Aplica um spinner indicando o loading do componente
     */
    isLoading: {
      type: Boolean,
      default: false,
      required: false,
    },
    /**
     * Define o título do componente
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * String ou Number representando o valor do componente
     */
    value: {
      type: [String, Number],
      required: true,
    },
    /**
     * Define a cor a ser aplicada ao componente (value)
     */
    color: {
      type: String,
      required: false,
      default: 'primary',
    },
    /**
     * Define a largura do componente
     */
    width: {
      type: [String, Number],
      required: false,
      default: null,
    },
    /**
     * Aplica a variação small ao componente
     */
    small: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Aplica sombra ao card
     */
    shadow: {
      type: Boolean,
      default: false,
    },
    /**
     * Aplica o estilo de skeleton-loading ao componente
     */
    skeletonLoading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    classes() {
      return {
        'zoox-component': true,
        'zoox-info-card': true,
        small: this.small,
      }
    },
    minHeight() {
      return this.small ? '60px' : '100px'
    },
  },
}
</script>

<style lang="scss">
.zoox-info-card {
  display: grid;
  place-items: center;

  .card-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .card-title {
    color: var(--black-300);
    text-transform: uppercase;
    font-weight: 400;
  }

  .card-value {
    color: var(--primary);
  }

  &:not(.small) {
    max-height: 100px;
    min-height: 100px;

    .card-value {
      font-size: 3rem;
    }
  }

  &.small {
    min-height: 60px;
    max-height: 60px;

    .card-value {
      font-weight: bold;
      font-size: 1.125;
    }
  }
}

.skeleton-loader {
  background: rgba(0, 0, 0, 0.12);
  border-radius: var(--component-card-radius);
  width: 150px;
  height: 90px;
  position: relative;

  &.small {
    width: 120px;
    height: 60px;
  }

  &::after {
    content: '';
    height: 100%;
    width: 100%;
    border-radius: var(--component-card-radius);
    background: linear-gradient(
      90deg,
      hsla(0, 0%, 100%, 0),
      hsla(0, 0%, 100%, 0.5),
      hsla(0, 0%, 100%, 0)
    );
    position: absolute;
    transform: translateX(-40%);
    left: 0;
    right: 0;
    top: 0;
    animation: loading 1.5s ease-in-out infinite alternate;
  }
}

@keyframes loading {
  100% {
    transform: translateX(40%);
  }
}
</style>
